import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/config";
import { useLang } from "@/i18n/LanguageContext";
import { VOLUME_VALUES } from "@/i18n/translations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";

const EMPTY = {
  name: "", business_name: "", email: "", phone: "", monthly_volume: "", message: "",
};

export default function QuoteForm({ compact = false, source = "website" }) {
  const { d, lang } = useLang();
  const f = d.form;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.business_name || !form.email || !form.phone || !form.monthly_volume) {
      toast.error(f.errFill);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, language: lang, source });
      setDone(true);
      setForm(EMPTY);
      toast.success(f.errSuccess);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || f.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="quote-success"
        className="rounded-2xl border border-accent/30 bg-white p-10 text-center shadow-xl"
      >
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-accent/10">
          <CheckCircle2 className="h-9 w-9 text-accent" />
        </div>
        <h3 className="font-display text-2xl font-semibold text-foreground">{f.successTitle}</h3>
        <p className="mt-2 text-muted-foreground">{f.successMsg}</p>
        <Button
          data-testid="quote-reset-button"
          variant="outline"
          className="mt-6"
          onClick={() => setDone(false)}
        >
          {f.successBtn}
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="quote-form"
      className="rounded-2xl border border-border bg-white p-6 shadow-2xl sm:p-8"
    >
      {!compact && (
        <div className="mb-6">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary">{f.tag}</p>
          <h3 className="mt-1 font-display text-2xl font-semibold text-foreground">{f.heading}</h3>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={f.name} required>
          <Input data-testid="input-name" value={form.name} onChange={set("name")} placeholder={f.namePh} />
        </Field>
        <Field label={f.business} required>
          <Input data-testid="input-business" value={form.business_name} onChange={set("business_name")} placeholder={f.businessPh} />
        </Field>
        <Field label={f.email} required>
          <Input data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder={f.emailPh} />
        </Field>
        <Field label={f.phone} required>
          <Input data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder={f.phonePh} />
        </Field>
        <div className="sm:col-span-2">
          <Label className="mb-1.5 block text-sm font-medium">{f.volume} <span className="text-primary">*</span></Label>
          <Select value={form.monthly_volume} onValueChange={set("monthly_volume")}>
            <SelectTrigger data-testid="select-volume">
              <SelectValue placeholder={f.volumePh} />
            </SelectTrigger>
            <SelectContent>
              {VOLUME_VALUES.map((v, i) => (
                <SelectItem key={v} value={v} data-testid={`volume-option-${i}`}>{f.volumes[i]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Field label={f.message}>
            <Textarea data-testid="input-message" value={form.message} onChange={set("message")} placeholder={f.messagePh} rows={3} />
          </Field>
        </div>
      </div>
      <Button
        data-testid="quote-submit-button"
        type="submit"
        disabled={loading}
        className="mt-6 h-12 w-full rounded-xl bg-primary text-base font-semibold text-primary-foreground transition-colors hover:bg-[hsl(326,100%,42%)] hover:text-white"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : f.submit}
      </Button>
      <p className="mt-3 text-center text-xs text-muted-foreground">{f.disclaimer}</p>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="mb-1.5 block text-sm font-medium">
        {label} {required && <span className="text-primary">*</span>}
      </Label>
      {children}
    </div>
  );
}
